<script lang="ts">
  import { createForm } from 'svelte-forms-lib'
  import { DoubleBounce } from 'svelte-loading-spinners'
  import { fieldErrorsOrUndefined } from '../utilities/forms'
  import * as T from 'io-ts'
  import { withMessage, NonEmptyString } from 'io-ts-types'
  import FormGroup from './EmailSignUpFormGroup.svelte'
  import CtaButton from '../Generic/CtaButton.svelte'
  import { auth } from '../Firebase'
  import { createEmailUser } from '../utilities/backendApi'
  import { sendEmailVerification, signInWithEmailAndPassword } from 'firebase/auth'
  import { SupportEmail } from '../utilities/Constants'
  import type { EmailSignUpFormProps } from './EmailSignUpFormProps'
  import { generateCurrentAppLocation } from '../utilities/helpers'

  let initialValues: EmailSignUpFormProps = {
    name: '' as NonEmptyString,
    email: '' as NonEmptyString,
    password: '' as NonEmptyString,
    passwordConfirmation: '' as NonEmptyString,
  }

  let message = ''
  let loading = false
  const FormT = T.type({
    name: withMessage(NonEmptyString, () => 'Please enter your name.'),
    email: withMessage(NonEmptyString, () => 'Please enter your email Address.'),
    password: withMessage(NonEmptyString, () => 'Please enter a password.'),
    passwordConfirmation: withMessage(NonEmptyString, () => 'Please enter your password confirmation.'),
  })

  const { form, errors, touched, isValid, isSubmitting, handleChange, handleSubmit } = createForm({
    initialValues,
    validate: values => fieldErrorsOrUndefined(T.partial(FormT.props), values),
    onSubmit: async (values: T.TypeOf<typeof FormT>) => {
      loading = true
      try {
        const result = await createEmailUser(values)
        if (result.status === 'Error') {
          message = result.error
        } else {
          message = `Successfully Signed Up!`
          await signInWithEmailAndPassword(auth, values.email, values.password)
          if (auth.currentUser) {
            await sendEmailVerification(auth.currentUser, {
              url: generateCurrentAppLocation(document),
            })
          }
        }
      } catch (error: unknown) {
        message = `I'm afraid something went wrong. Please contact ${SupportEmail} and we will try to help.`
      }
      loading = false
    },
  })

  touched.subscribe(() => (message = ''))
</script>

{#if loading}
  <div class="spinner">
    <DoubleBounce color="#4b4b4b" size="2" unit="em" />
  </div>
{/if}

<div id="container">
  <form on:submit={handleSubmit}>
    <div class="grid">
      <FormGroup key="name" label="Name" {form} {errors} {handleChange} />
      <FormGroup key="email" label="Email" {form} {errors} {handleChange} />
      <FormGroup key="password" label="Password" password {form} {errors} {handleChange} />
      <FormGroup
        key="passwordConfirmation"
        label="Password Confirmation"
        password
        {form}
        {errors}
        {handleChange}
      />
      <div id="submit-buttons">
        <CtaButton type="submit" disabled={!$isValid || $isSubmitting}>Create Account</CtaButton>
      </div>
      {#if message}
        <h1>{message}</h1>
      {/if}
    </div>
  </form>
</div>

<style>
  .spinner {
    display: flex;
    position: absolute;
    justify-content: center;
    text-align: center;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    font-size: 60px;
  }
  .grid {
    padding: 2em;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    align-items: center;
  }

  #submit-buttons {
    grid-column: 1 / span 3;
    display: flex;
    justify-content: space-evenly;
    margin: 2em 0em;
  }

  h1 {
    grid-column: 1 / span 3;
  }

  #container {
    width: 100%;
    max-width: 75em;
  }
</style>
